// src/ai-agent/card-schemas.ts
import { z } from "zod";
var ConfigurationDraftSchema = z.object({
  key: z.string(),
  newValue: z.union([z.string(), z.number(), z.boolean(), z.array(z.unknown())]),
  previousValue: z.union([z.string(), z.number(), z.boolean(), z.array(z.unknown())]).optional(),
  category: z.string(),
  dataType: z.string(),
  description: z.string().optional(),
  validationResult: z.object({
    isValid: z.boolean(),
    message: z.string().optional(),
    errors: z.array(z.string()).optional()
  }).optional()
});
var ConfigCardPayloadSchema = z.union([
  ConfigurationDraftSchema,
  z.array(ConfigurationDraftSchema)
]);
var AgentCardSchema = z.object({
  kind: z.enum(["configCard", "bookingCard", "infoCard"]),
  payload: z.unknown()
});
var TypedAgentCardSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("configCard"),
    payload: ConfigCardPayloadSchema
  }),
  z.object({
    kind: z.literal("bookingCard"),
    payload: z.object({
      // Future: booking confirmation data
      serviceId: z.string(),
      workerId: z.string().optional(),
      startTime: z.string()
    })
  }),
  z.object({
    kind: z.literal("infoCard"),
    payload: z.object({
      title: z.string(),
      message: z.string(),
      type: z.enum(["info", "warning", "error", "success"])
    })
  })
]);

// src/ai-agent/client-tool-schemas.ts
import { z as z2 } from "zod";
var UpdateCalendarViewSchema = z2.object({
  date: z2.string().optional().describe(
    "Optional: ISO 8601 date string (e.g., '2025-10-27') or relative date like 'tomorrow', 'next monday'"
  ),
  viewMode: z2.enum(["list", "daily", "worker", "3-day", "week", "month"]).optional().describe("Optional: Calendar view mode to switch to")
});
var NavigateToPageSchema = z2.object({
  page: z2.enum([
    "dashboard",
    "calendar",
    "appointments",
    "customers",
    "services",
    "team",
    "settings",
    "reports",
    "ai-chat"
  ]).describe("Page to navigate to"),
  section: z2.string().optional().describe("Optional section within the page")
});
var ShowAppointmentDetailSchema = z2.object({
  appointmentId: z2.string().describe("ID of the appointment to display"),
  action: z2.enum(["view", "edit"]).optional().describe("Action to perform on the appointment")
});
var HighlightElementSchema = z2.object({
  elementId: z2.string().describe("CSS selector or element ID to highlight"),
  duration: z2.number().optional().describe("Duration in milliseconds to show highlight")
});
var ShowNotificationSchema = z2.object({
  message: z2.string().describe("Message to display"),
  type: z2.enum(["info", "success", "warning", "error"]).describe("Type of notification"),
  duration: z2.number().optional().describe("Duration in milliseconds")
});
var OpenSettingsPanelSchema = z2.object({
  panel: z2.enum([
    "general",
    "booking",
    "hours",
    "services",
    "team",
    "notifications",
    "integrations",
    "billing"
  ]).describe("Settings panel to open")
});
var SetReportsDateRangeSchema = z2.object({
  preset: z2.enum([
    "last 7 days",
    "last 30 days",
    "last 90 days",
    "last 180 days",
    "last 365 days",
    "last week",
    "last month",
    "last quarter",
    "last year"
  ]).optional().describe("Preset time range (mutually exclusive with startDate/endDate)"),
  startDate: z2.string().optional().describe("Custom start date in ISO format YYYY-MM-DD (requires endDate)"),
  endDate: z2.string().optional().describe("Custom end date in ISO format YYYY-MM-DD (requires startDate)"),
  comparisonStrategy: z2.enum(["previous period", "same period last year", "year over year", "custom"]).optional().describe("How to calculate comparison period"),
  comparisonStartDate: z2.string().optional().describe("Custom comparison start date (only with comparisonStrategy: custom)"),
  comparisonEndDate: z2.string().optional().describe("Custom comparison end date (only with comparisonStrategy: custom)")
});
var GetDetailedViewContextSchema = z2.object({});

// src/ai-agent/client-tools.ts
var updateCalendarViewTool = {
  name: "updateCalendarView",
  description: "Update the calendar view by navigating to a specific date and/or changing the view mode. Use this when the user asks to view appointments for a particular day or switch calendar views. Both parameters are optional but at least one must be provided.",
  schema: UpdateCalendarViewSchema
};
var navigateToPageTool = {
  name: "navigateToPage",
  description: "Navigate to a specific page or section in the application. Use this when the user wants to go to a different part of the app (dashboard, customers, services, settings, etc.).",
  schema: NavigateToPageSchema
};
var showAppointmentDetailTool = {
  name: "showAppointmentDetail",
  description: "Show detailed information about a specific appointment in a modal or panel. Use this when the user wants to view or edit a specific appointment.",
  schema: ShowAppointmentDetailSchema
};
var highlightElementTool = {
  name: "highlightElement",
  description: "Highlight or draw attention to a specific UI element on the screen. Use this when guiding the user to find something in the interface.",
  schema: HighlightElementSchema
};
var showNotificationTool = {
  name: "showNotification",
  description: "Display a notification or toast message to the user. Use this to confirm actions, show warnings, or provide information.",
  schema: ShowNotificationSchema
};
var openSettingsPanelTool = {
  name: "openSettingsPanel",
  description: "Open a specific settings panel (general, booking, hours, services, team, etc.). Use this when the user wants to configure or modify settings.",
  schema: OpenSettingsPanelSchema
};
var setReportsDateRangeTool = {
  name: "setReportsDateRange",
  description: "Change the date range and comparison period for reports. Use this when the user wants to view reports for a specific time period. Supports presets (e.g., 'last 30 days', 'last month') or custom date ranges. Can also set comparison strategy for year-over-year or sequential comparisons.",
  schema: SetReportsDateRangeSchema
};
var getDetailedViewContextTool = {
  name: "getDetailedViewContext",
  description: "Get detailed information about what the user is currently viewing. Use this when the user asks about current screen data (reports, appointments, calendar, etc.). Returns comprehensive details about visible data, metrics, and status. This retrieves the full context, not just the summary shown in VIEW CONTEXT.",
  schema: GetDetailedViewContextSchema
};
var CLIENT_TOOLS_REGISTRY = {
  updateCalendarView: updateCalendarViewTool,
  navigateToPage: navigateToPageTool,
  showAppointmentDetail: showAppointmentDetailTool,
  highlightElement: highlightElementTool,
  showNotification: showNotificationTool,
  openSettingsPanel: openSettingsPanelTool,
  setReportsDateRange: setReportsDateRangeTool,
  getDetailedViewContext: getDetailedViewContextTool
};
var ALL_CLIENT_TOOLS = Object.values(CLIENT_TOOLS_REGISTRY);

// src/ai-agent/graph-state-schemas.ts
import { z as z4 } from "zod";

// src/ai-agent/message-schemas.ts
import { z as z3 } from "zod";
var MessageRoleSchema = z3.enum(["user", "assistant", "system"]);
var BaseMessageSchema = z3.object({
  id: z3.string(),
  role: MessageRoleSchema,
  content: z3.string(),
  timestamp: z3.string().datetime()
});
var AgentMessageSchema = BaseMessageSchema.extend({
  ui: z3.object({
    card: z3.object({
      kind: z3.string(),
      payload: z3.unknown()
    }).optional()
  }).optional()
});

// src/ai-agent/graph-state-schemas.ts
var AgentUserContextSchema = z4.object({
  userId: z4.string(),
  email: z4.string().email(),
  firstName: z4.string(),
  lastName: z4.string(),
  roles: z4.array(z4.string())
});
var UIStateSchema = z4.object({
  card: z4.object({
    kind: z4.string(),
    payload: z4.unknown()
  }).optional(),
  loading: z4.boolean().optional(),
  error: z4.string().optional()
}).optional();
var AppStateSchema = z4.object({
  // Messages
  messages: z4.array(BaseMessageSchema),
  // Context
  salonId: z4.string(),
  userId: z4.string(),
  userContext: AgentUserContextSchema,
  // Configuration draft state
  configDraft: z4.union([ConfigurationDraftSchema, z4.array(ConfigurationDraftSchema)]).optional(),
  // UI state for frontend
  ui: UIStateSchema,
  // Thread management
  threadId: z4.string(),
  // Routing state
  lastNodeExecuted: z4.string().optional(),
  nextNode: z4.string().optional(),
  goto: z4.string().optional(),
  // Error handling
  error: z4.string().optional()
});
var NodeResultSchema = z4.object({
  goto: z4.string().optional(),
  update: z4.record(z4.string(), z4.unknown()).optional(),
  interrupt: z4.boolean().optional()
});

// src/ai-agent/thread-schemas.ts
import { z as z5 } from "zod";
var ThreadStatusSchema = z5.enum([
  "active",
  "waiting_for_user",
  "processing",
  "completed",
  "error"
]);
var AgentThreadSchema = z5.object({
  id: z5.string(),
  salonId: z5.string(),
  userId: z5.string(),
  status: ThreadStatusSchema,
  messages: z5.array(AgentMessageSchema),
  state: z5.record(z5.string(), z5.unknown()).optional(),
  metadata: z5.object({
    title: z5.string().optional(),
    lastAgentType: z5.string().optional(),
    totalMessages: z5.number()
  }).optional(),
  createdAt: z5.string().datetime(),
  updatedAt: z5.string().datetime()
});
var CreateThreadInputSchema = z5.object({
  salonId: z5.string(),
  initialMessage: z5.string().optional()
});
var SendMessageInputSchema = z5.object({
  threadId: z5.string().optional(),
  salonId: z5.string(),
  message: z5.string().min(1, "Message cannot be empty"),
  viewContext: z5.string().optional()
});
var AgentActionInputSchema = z5.object({
  threadId: z5.string(),
  action: z5.enum(["confirm", "decline", "edit"]),
  payload: z5.unknown().optional()
});

// src/appointment-utils.ts
function isAppointmentCompleted(appointment) {
  return appointment.outcome === "COMPLETED" && appointment.bookingStatus !== "DECLINED";
}
function isAppointmentCancelled(appointment) {
  return appointment.bookingStatus === "DECLINED";
}
var COMPLETED_APPOINTMENT_FILTER = {
  outcome: "COMPLETED",
  bookingStatus: { not: "DECLINED" }
};
function sqlCompletedCondition(alias) {
  const col = (name) => alias ? `${alias}."${name}"` : `"${name}"`;
  return `${col("outcome")} = 'COMPLETED' AND ${col("bookingStatus")} != 'DECLINED'`;
}
var SQL_COMPLETED_APPOINTMENT_CONDITION = sqlCompletedCondition();

// src/audit/schemas.ts
import { z as z6 } from "zod";
var AppointmentAuditMetadataSchema = z6.object({
  customerName: z6.string().min(1, "Customer name is required for appointment auditing")
});

// src/audit/validators.ts
function validateAppointmentAuditMetadata(metadata) {
  return AppointmentAuditMetadataSchema.parse(metadata);
}
function createAppointmentAuditMetadata(customerFirstName, customerLastName) {
  const customerName = `${customerFirstName} ${customerLastName}`.trim();
  return { customerName };
}

// src/authorization/actions.ts
var AUTHORIZATION_ACTIONS = {
  // CRUD operations
  CREATE: "create",
  READ: "read",
  UPDATE: "update",
  DELETE: "delete",
  // Full access
  MANAGE: "manage",
  // Specialized actions
  ASSIGN: "assign",
  // Assign workers to appointments
  APPROVE: "approve",
  // Approve time off, changes
  EXPORT: "export",
  // Export data/reports
  VIEW_ANALYTICS: "view_analytics",
  VIEW_SCHEDULE: "view_schedule"
};
function isAuthorizationAction(action) {
  return Object.values(AUTHORIZATION_ACTIONS).includes(action);
}

// src/authorization/subjects.ts
var AUTHORIZATION_SUBJECTS = {
  // Core entities
  APPOINTMENT: "Appointment",
  MY_APPOINTMENT: "MyAppointment",
  WORKER: "Worker",
  WORKER_SALON: "WorkerSalon",
  CUSTOMER: "Customer",
  SERVICE: "Service",
  SERVICE_CATEGORY: "ServiceCategory",
  SERVICE_TEMPLATE: "ServiceTemplate",
  DISCOUNT: "Discount",
  // Salon management
  SALON: "Salon",
  SALON_SETTINGS: "SalonSettings",
  SALON_CONFIGURATION: "SalonConfiguration",
  PREMIUM_SALON_CONFIGURATION: "PremiumSalonConfiguration",
  SALON_REVIEW: "SalonReview",
  PROMO_EVENT: "PromoEvent",
  GOOGLE_BUSINESS_CONNECTION: "GoogleBusinessConnection",
  GOOGLE_BUSINESS_MANAGER_ACCOUNT: "GoogleBusinessManagerAccount",
  // Time management
  HOLIDAY: "Holiday",
  AVAILABILITY: "Availability",
  SCHEDULE: "Schedule",
  // Analytics & Reporting
  REPORT: "Report",
  ANALYTICS: "Analytics",
  AUDIT_LOG: "AuditLog",
  // User management
  USER_ROLE: "UserRole",
  MEMBER_PASSWORD: "MemberPassword",
  // Partner management
  PARTNER_GROUP: "PartnerGroup",
  PARTNER_MEMBER: "PartnerMember",
  PARTNER_FRANCHISE_ASSIGNMENT: "PartnerFranchiseAssignment",
  FRANCHISE: "Franchise",
  // System
  ALL: "all"
};
function isAuthorizationSubject(subject) {
  return Object.values(AUTHORIZATION_SUBJECTS).includes(subject);
}

// src/authorization/abilities.ts
function defineOwnerAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.ALL);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
}
function defineManagerAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.ASSIGN, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.ASSIGN, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.VIEW_SCHEDULE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.CUSTOMER);
  cannot(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_SETTINGS);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON_SETTINGS);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.USER_ROLE);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.ANALYTICS);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.GOOGLE_BUSINESS_CONNECTION);
  cannot(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.USER_ROLE);
  cannot(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.REPORT);
}
function defineWorkerAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  cannot(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.VIEW_SCHEDULE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE_CATEGORY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_SETTINGS);
}
function defineFranchiseAdminAbilities(can, cannot) {
  defineOwnerAbilities(can, cannot);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
}
function definePartnerGroupAdminAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PARTNER_GROUP);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PARTNER_MEMBER);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PARTNER_FRANCHISE_ASSIGNMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.FRANCHISE);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.FRANCHISE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.SALON);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SERVICE_CATEGORY);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.AVAILABILITY);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.ANALYTICS);
  can(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.REPORT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MEMBER_PASSWORD);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
}
function definePartnerGroupMemberAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PARTNER_GROUP);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PARTNER_MEMBER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.PARTNER_MEMBER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.FRANCHISE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE_CATEGORY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  cannot(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.WORKER);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.VIEW_SCHEDULE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.ANALYTICS);
  cannot(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.REPORT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MEMBER_PASSWORD);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
}
function defineSuperAdminAbilities(can) {
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.ALL);
}
var ROLE_ABILITIES = {
  owner: defineOwnerAbilities,
  manager: defineManagerAbilities,
  worker: defineWorkerAbilities,
  franchiseAdmin: defineFranchiseAdminAbilities,
  partnerGroupAdmin: definePartnerGroupAdminAbilities,
  partnerGroupMember: definePartnerGroupMemberAbilities,
  superAdmin: defineSuperAdminAbilities
};

// src/authorization/ability-factory.ts
import { AbilityBuilder, createMongoAbility } from "@casl/ability";
function defineEmptyAbilities(_can, _cannot) {
}
function getSalonAbilityFunction(role) {
  switch (role) {
    case "SUPER_ADMIN":
    case "ADMIN":
    case "GROUP_ADMIN":
    case "GROUP_MEMBER":
    case "FRANCHISE_ADMIN":
      return defineFranchiseAdminAbilities;
    case "OWNER":
      return defineOwnerAbilities;
    case "MANAGER":
    case "FRANCHISE_VIEWER":
      return defineManagerAbilities;
    case "WORKER":
      return defineWorkerAbilities;
    default:
      return defineEmptyAbilities;
  }
}
function getPartnerGroupAbilityFunction(role) {
  switch (role) {
    case "SUPER_ADMIN":
    case "ADMIN":
    case "GROUP_ADMIN":
      return definePartnerGroupAdminAbilities;
    case "GROUP_MEMBER":
      return definePartnerGroupMemberAbilities;
    case "FRANCHISE_VIEWER":
      return definePartnerGroupMemberAbilities;
    default:
      return defineEmptyAbilities;
  }
}
function createSalonAbilityForUser(user, salonId) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);
  if (!user) {
    return build();
  }
  if (user.role.includes("ADMIN") || user.role.includes("SUPER_ADMIN")) {
    defineSuperAdminAbilities(can);
    return build();
  }
  let role = null;
  for (const franchiseAccess of user.franchiseAccesses) {
    if (franchiseAccess.isActive) {
      const foundSalonAccess = franchiseAccess.salons.find(
        (salon) => salon.salonId === salonId && salon.isActive
      );
      if (foundSalonAccess) {
        role = foundSalonAccess.role;
        break;
      }
    }
  }
  for (const partnerAccess of user.partnerGroupMemberships ?? []) {
    if (partnerAccess.isActive) {
      const salons = partnerAccess.assignedFranchises?.flatMap((franchise) => franchise.salons);
      const foundSalonAccess = salons?.find((salon) => salon.salonId === salonId && salon.isActive);
      if (foundSalonAccess) {
        role = foundSalonAccess.role;
      }
    }
  }
  if (!role) {
    return build();
  }
  const abilityFunction = getSalonAbilityFunction(role);
  abilityFunction(can, cannot);
  return build();
}
function createPartnerGroupAbilityForUser(user, groupId) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);
  if (!user) {
    return build();
  }
  if (user.role.includes("ADMIN") || user.role.includes("SUPER_ADMIN")) {
    defineSuperAdminAbilities(can);
    return build();
  }
  const partnerAccess = user.partnerGroupMemberships?.find(
    (membership) => membership.groupId === groupId && membership.isActive
  );
  if (!partnerAccess) {
    return build();
  }
  const abilityFunction = getPartnerGroupAbilityFunction(partnerAccess.role);
  abilityFunction(can, cannot);
  return build();
}
function createSystemAbility() {
  const { can, build } = new AbilityBuilder(createMongoAbility);
  defineSuperAdminAbilities(can);
  return build();
}
function createAbilityForUser(user) {
  const { can, build } = new AbilityBuilder(createMongoAbility);
  if (!user) {
    return build();
  }
  if (user.role.includes("ADMIN") || user.role.includes("SUPER_ADMIN")) {
    defineSuperAdminAbilities(can);
  }
  return build();
}

// src/authorization/types.ts
var detectSubjectType = (item) => {
  if (typeof item === "string") {
    return item;
  }
  return item.constructor;
};

// src/configuration/configuration-enums.ts
var OnlineBookingMode = /* @__PURE__ */ ((OnlineBookingMode2) => {
  OnlineBookingMode2["AUTO_ACCEPT"] = "auto_accept";
  OnlineBookingMode2["AUTO_ACCEPT_ASSIGN"] = "auto_accept_assign";
  OnlineBookingMode2["MANUAL"] = "manual";
  return OnlineBookingMode2;
})(OnlineBookingMode || {});
var BookingBehaviorMode = /* @__PURE__ */ ((BookingBehaviorMode2) => {
  BookingBehaviorMode2["MANUAL"] = "manual";
  BookingBehaviorMode2["AUTO"] = "auto";
  BookingBehaviorMode2["AI"] = "ai";
  return BookingBehaviorMode2;
})(BookingBehaviorMode || {});
var OutcomeTrackingMode = /* @__PURE__ */ ((OutcomeTrackingMode2) => {
  OutcomeTrackingMode2["AUTO"] = "auto";
  OutcomeTrackingMode2["MANUAL"] = "manual";
  return OutcomeTrackingMode2;
})(OutcomeTrackingMode || {});
var ServiceSelectionMode = /* @__PURE__ */ ((ServiceSelectionMode2) => {
  ServiceSelectionMode2["SINGLE"] = "single";
  ServiceSelectionMode2["MULTIPLE"] = "multiple";
  return ServiceSelectionMode2;
})(ServiceSelectionMode || {});
var WorkerSelectionMode = /* @__PURE__ */ ((WorkerSelectionMode2) => {
  WorkerSelectionMode2["PRIMARY_SERVICE"] = "primary_service";
  WorkerSelectionMode2["ALL_SERVICES"] = "all_services";
  WorkerSelectionMode2["INDIVIDUAL_ASSIGNMENT"] = "individual_assignment";
  return WorkerSelectionMode2;
})(WorkerSelectionMode || {});
var NotificationChannel = /* @__PURE__ */ ((NotificationChannel2) => {
  NotificationChannel2["EMAIL"] = "email";
  NotificationChannel2["SMS"] = "sms";
  NotificationChannel2["WHATSAPP"] = "whatsapp";
  return NotificationChannel2;
})(NotificationChannel || {});

// src/configuration/configuration-defaults.ts
var CONFIG_DEFAULTS = {
  // Appointment Limits
  "appointment_limits.capacity_mode": "simple",
  "appointment_limits.concurrent_max": 10,
  "appointment_limits.daily_max": 100,
  "appointment_limits.concurrent_infinity": true,
  "appointment_limits.daily_infinity": true,
  "appointment_limits.duration_aware": true,
  // Appointment Buffer
  "appointment_buffer.preparation_time": 0,
  "appointment_buffer.cleanup_time": 0,
  // Loyalty
  "loyalty.points_per_dollar": 1,
  "loyalty.points_per_visit": 0,
  // No visit points by default
  "loyalty.enabled": false,
  "loyalty.minimum_spend_threshold": 0,
  // $0 minimum, always earn points
  // Online Booking Configuration
  "online_booking.enabled": true,
  "online_booking.slots_per_hour": 2,
  "online_booking.lead_minutes_from_now": 0,
  "online_booking.lead_minutes_from_opening": 30,
  "online_booking.trail_minutes_before_closing": 60,
  "online_booking.mode": "manual" /* MANUAL */,
  "online_booking.waiting_list_enabled": false,
  "online_booking.advance_booking_days": 30,
  "online_booking.worker_selection_enabled": true,
  "online_booking.service_selection_mode": "multiple" /* MULTIPLE */,
  "online_booking.worker_selection_mode": "primary_service" /* PRIMARY_SERVICE */,
  "online_booking.allow_booking_past_closing": false,
  "online_booking.show_service_duration": true,
  "online_booking.bank_transfer_enabled": true,
  // Booking Behavior Configuration
  "booking_behavior.mode": "manual" /* MANUAL */,
  "booking_behavior.manual.count_pending_toward_capacity": false,
  "booking_behavior.auto.arrange_workers": true,
  "booking_behavior.auto.confirm_immediately": true,
  "booking_behavior.auto.batch_confirm_interval": 15,
  "booking_behavior.ai.natural_language_rules": "",
  "booking_behavior.ai.smart_gap_filling": true,
  "booking_behavior.ai.service_recommendations": true,
  // Salon Configuration
  "salon.appointment_worker_assignment_enabled": false,
  "salon.outcome_tracking_enabled": true,
  "salon.outcome_tracking_mode": "auto" /* AUTO */,
  "salon.loyalty_program_enabled": true,
  // Notification Limits (-1 = unlimited)
  "notification_limits.email_monthly_limit": -1,
  "notification_limits.sms_monthly_limit": 0,
  "notification_limits.whatsapp_monthly_limit": 0,
  // Customer Notification Chains (external - sent to customers)
  "customer_notifications.appointment_confirmation_chain": [["email"]],
  "customer_notifications.appointment_reminder_chain": [["email"]],
  "customer_notifications.waitlist_approved_chain": [["email"]],
  "customer_notifications.fallback_delay_ms": 5e3,
  "customer_notifications.max_chain_length": 4,
  // User Notification Chains (internal - sent to salon staff)
  "user_notifications.new_appointment_chain": [["email"], ["push"]],
  "user_notifications.appointment_cancelled_chain": [["email"], ["push"]],
  "user_notifications.daily_summary_chain": [["email"], ["push"]],
  "user_notifications.fallback_delay_ms": 3e3,
  "user_notifications.max_chain_length": 3,
  // Premium Features
  "premium_features.appointment_history_limit_days": 3,
  "premium_features.customer_query_limit_enabled": true,
  "premium_features.reports_access_limit_enabled": true,
  "premium_features.ai_chat_enabled": false,
  "premium_features.ai_monthly_credit_default": 0,
  // UI Feature Flags
  "ui.report.enabled": true
};
var CONFIG_STRING_DEFAULTS = {
  // Appointment Limits
  "appointment_limits.capacity_mode": "simple",
  "appointment_limits.concurrent_max": "10",
  "appointment_limits.daily_max": "100",
  "appointment_limits.concurrent_infinity": "true",
  "appointment_limits.daily_infinity": "true",
  "appointment_limits.duration_aware": "true",
  // Appointment Buffer
  "appointment_buffer.preparation_time": "0",
  "appointment_buffer.cleanup_time": "0",
  // Loyalty
  "loyalty.points_per_dollar": "1",
  "loyalty.points_per_visit": "0",
  "loyalty.enabled": "false",
  "loyalty.minimum_spend_threshold": "0",
  // Online Booking Configuration
  "online_booking.enabled": "true",
  "online_booking.slots_per_hour": "2",
  "online_booking.lead_minutes_from_now": "0",
  "online_booking.lead_minutes_from_opening": "30",
  "online_booking.trail_minutes_before_closing": "60",
  "online_booking.mode": "manual" /* MANUAL */,
  "online_booking.waiting_list_enabled": "false",
  "online_booking.advance_booking_days": "30",
  "online_booking.worker_selection_enabled": "true",
  "online_booking.service_selection_mode": "multiple" /* MULTIPLE */,
  "online_booking.worker_selection_mode": "primary_service" /* PRIMARY_SERVICE */,
  "online_booking.allow_booking_past_closing": "false",
  "online_booking.show_service_duration": "true",
  "online_booking.bank_transfer_enabled": "true",
  // Booking Behavior Configuration
  "booking_behavior.mode": "manual" /* MANUAL */,
  "booking_behavior.manual.count_pending_toward_capacity": "false",
  "booking_behavior.auto.arrange_workers": "true",
  "booking_behavior.auto.confirm_immediately": "true",
  "booking_behavior.auto.batch_confirm_interval": "15",
  "booking_behavior.ai.natural_language_rules": "",
  "booking_behavior.ai.smart_gap_filling": "true",
  "booking_behavior.ai.service_recommendations": "true",
  // Salon Configuration
  "salon.appointment_worker_assignment_enabled": "false",
  "salon.outcome_tracking_enabled": "true",
  "salon.outcome_tracking_mode": "auto" /* AUTO */,
  "salon.loyalty_program_enabled": "true",
  // Notification Limits (-1 = unlimited)
  "notification_limits.email_monthly_limit": "-1",
  "notification_limits.sms_monthly_limit": "0",
  "notification_limits.whatsapp_monthly_limit": "0",
  // Customer Notification Chains (external - sent to customers)
  "customer_notifications.appointment_confirmation_chain": '[["email"]]',
  "customer_notifications.appointment_reminder_chain": '[["email"]]',
  "customer_notifications.waitlist_approved_chain": '[["email"]]',
  "customer_notifications.fallback_delay_ms": "5000",
  "customer_notifications.max_chain_length": "4",
  // User Notification Chains (internal - sent to salon staff)
  "user_notifications.new_appointment_chain": '[["email"], ["push"]]',
  "user_notifications.appointment_cancelled_chain": '[["email"], ["push"]]',
  "user_notifications.daily_summary_chain": '[["email"], ["push"]]',
  "user_notifications.fallback_delay_ms": "3000",
  "user_notifications.max_chain_length": "3",
  // Premium Features
  "premium_features.appointment_history_limit_days": "3",
  "premium_features.customer_query_limit_enabled": "true",
  "premium_features.reports_access_limit_enabled": "true",
  "premium_features.ai_chat_enabled": "false",
  "premium_features.ai_monthly_credit_default": "0",
  // UI Feature Flags
  "ui.report.enabled": "true"
};
var CONFIG_CATEGORIES = {
  APPOINTMENT_LIMITS: "appointment_limits",
  BOOKING_POLICY: "booking_policy",
  APPOINTMENT_BUFFER: "appointment_buffer",
  LOYALTY: "loyalty",
  ONLINE_BOOKING: "online_booking",
  BOOKING_BEHAVIOR: "booking_behavior",
  SALON: "salon",
  NOTIFICATION_LIMITS: "notification_limits",
  CUSTOMER_NOTIFICATIONS: "customer_notifications",
  USER_NOTIFICATIONS: "user_notifications",
  PREMIUM_FEATURES: "premium_features",
  UI: "ui"
};
var UNLIMITED_CAPACITY = 999;

// src/configuration/configuration-keys.ts
var CONFIG_KEYS = {
  APPOINTMENT_LIMITS: {
    CAPACITY_MODE: "appointment_limits.capacity_mode",
    CONCURRENT_MAX: "appointment_limits.concurrent_max",
    DAILY_MAX: "appointment_limits.daily_max",
    CONCURRENT_INFINITY: "appointment_limits.concurrent_infinity",
    DAILY_INFINITY: "appointment_limits.daily_infinity",
    DURATION_AWARE: "appointment_limits.duration_aware"
  },
  APPOINTMENT_BUFFER: {
    PREPARATION_TIME: "appointment_buffer.preparation_time",
    CLEANUP_TIME: "appointment_buffer.cleanup_time"
  },
  LOYALTY: {
    POINTS_PER_DOLLAR: "loyalty.points_per_dollar",
    POINTS_PER_VISIT: "loyalty.points_per_visit",
    ENABLED: "loyalty.enabled",
    MINIMUM_SPEND_THRESHOLD: "loyalty.minimum_spend_threshold"
  },
  ONLINE_BOOKING: {
    ENABLED: "online_booking.enabled",
    SLOTS_PER_HOUR: "online_booking.slots_per_hour",
    LEAD_MINUTES_FROM_NOW: "online_booking.lead_minutes_from_now",
    LEAD_MINUTES_FROM_OPENING: "online_booking.lead_minutes_from_opening",
    TRAIL_MINUTES_BEFORE_CLOSING: "online_booking.trail_minutes_before_closing",
    MODE: "online_booking.mode",
    WAITING_LIST_ENABLED: "online_booking.waiting_list_enabled",
    ADVANCE_BOOKING_DAYS: "online_booking.advance_booking_days",
    WORKER_SELECTION_ENABLED: "online_booking.worker_selection_enabled",
    SERVICE_SELECTION_MODE: "online_booking.service_selection_mode",
    WORKER_SELECTION_MODE: "online_booking.worker_selection_mode",
    ALLOW_BOOKING_PAST_CLOSING: "online_booking.allow_booking_past_closing",
    SHOW_SERVICE_DURATION: "online_booking.show_service_duration",
    BANK_TRANSFER_ENABLED: "online_booking.bank_transfer_enabled"
  },
  BOOKING_BEHAVIOR: {
    MODE: "booking_behavior.mode",
    MANUAL_COUNT_PENDING_TOWARD_CAPACITY: "booking_behavior.manual.count_pending_toward_capacity",
    AUTO_ARRANGE_WORKERS: "booking_behavior.auto.arrange_workers",
    AUTO_CONFIRM_IMMEDIATELY: "booking_behavior.auto.confirm_immediately",
    AUTO_BATCH_CONFIRM_INTERVAL: "booking_behavior.auto.batch_confirm_interval",
    AI_NATURAL_LANGUAGE_RULES: "booking_behavior.ai.natural_language_rules",
    AI_SMART_GAP_FILLING: "booking_behavior.ai.smart_gap_filling",
    AI_SERVICE_RECOMMENDATIONS: "booking_behavior.ai.service_recommendations"
  },
  SALON: {
    APPOINTMENT_WORKER_ASSIGNMENT_ENABLED: "salon.appointment_worker_assignment_enabled",
    OUTCOME_TRACKING_ENABLED: "salon.outcome_tracking_enabled",
    OUTCOME_TRACKING_MODE: "salon.outcome_tracking_mode",
    LOYALTY_PROGRAM_ENABLED: "salon.loyalty_program_enabled"
  },
  NOTIFICATION_LIMITS: {
    EMAIL_MONTHLY_LIMIT: "notification_limits.email_monthly_limit",
    SMS_MONTHLY_LIMIT: "notification_limits.sms_monthly_limit",
    WHATSAPP_MONTHLY_LIMIT: "notification_limits.whatsapp_monthly_limit"
  },
  PREMIUM_FEATURES: {
    APPOINTMENT_HISTORY_LIMIT_DAYS: "premium_features.appointment_history_limit_days",
    CUSTOMER_QUERY_LIMIT_ENABLED: "premium_features.customer_query_limit_enabled",
    REPORTS_ACCESS_LIMIT_ENABLED: "premium_features.reports_access_limit_enabled",
    AI_CHAT_ENABLED: "premium_features.ai_chat_enabled",
    AI_MONTHLY_CREDIT_DEFAULT: "premium_features.ai_monthly_credit_default"
  },
  UI: {
    REPORT_ENABLED: "ui.report.enabled"
  }
};
function isValidConfigurationKey(key) {
  return key in CONFIG_DEFAULTS_MAP;
}
var ALL_CONFIG_KEYS = Object.values(CONFIG_KEYS).flatMap(
  (category) => Object.values(category)
);
var CONFIG_DEFAULTS_MAP = {
  // Appointment Limits
  "appointment_limits.capacity_mode": "simple",
  "appointment_limits.concurrent_max": 4,
  "appointment_limits.daily_max": 50,
  "appointment_limits.concurrent_infinity": false,
  "appointment_limits.daily_infinity": false,
  "appointment_limits.duration_aware": true,
  // Appointment Buffer
  "appointment_buffer.preparation_time": 5,
  "appointment_buffer.cleanup_time": 5,
  // Loyalty
  "loyalty.points_per_dollar": 1,
  "loyalty.points_per_visit": 0,
  "loyalty.enabled": true,
  "loyalty.minimum_spend_threshold": 0,
  // Online Booking
  "online_booking.enabled": true,
  "online_booking.slots_per_hour": 2,
  "online_booking.lead_minutes_from_now": 0,
  "online_booking.lead_minutes_from_opening": 15,
  "online_booking.trail_minutes_before_closing": 15,
  "online_booking.mode": "auto_accept" /* AUTO_ACCEPT */,
  "online_booking.waiting_list_enabled": true,
  "online_booking.advance_booking_days": 30,
  "online_booking.worker_selection_enabled": true,
  "online_booking.service_selection_mode": "single" /* SINGLE */,
  "online_booking.worker_selection_mode": "primary_service" /* PRIMARY_SERVICE */,
  "online_booking.allow_booking_past_closing": false,
  "online_booking.show_service_duration": true,
  "online_booking.bank_transfer_enabled": true,
  // Booking Behavior
  "booking_behavior.mode": "manual" /* MANUAL */,
  "booking_behavior.manual.count_pending_toward_capacity": false,
  "booking_behavior.auto.arrange_workers": true,
  "booking_behavior.auto.confirm_immediately": true,
  "booking_behavior.auto.batch_confirm_interval": 15,
  "booking_behavior.ai.natural_language_rules": "",
  "booking_behavior.ai.smart_gap_filling": true,
  "booking_behavior.ai.service_recommendations": true,
  // Salon
  "salon.appointment_worker_assignment_enabled": true,
  "salon.outcome_tracking_enabled": true,
  "salon.outcome_tracking_mode": "auto" /* AUTO */,
  "salon.loyalty_program_enabled": true,
  // Notification Limits (-1 = unlimited)
  "notification_limits.email_monthly_limit": -1,
  "notification_limits.sms_monthly_limit": 100,
  "notification_limits.whatsapp_monthly_limit": 50,
  // User Notifications
  "user_notifications.new_appointment_chain": [["email"]],
  "user_notifications.appointment_cancelled_chain": [["email"]],
  "user_notifications.daily_summary_chain": [["email"]],
  "user_notifications.fallback_delay_ms": 1e3,
  "user_notifications.max_chain_length": 5,
  // Customer Notifications
  "customer_notifications.appointment_confirmation_chain": [["email"]],
  "customer_notifications.appointment_reminder_chain": [["email"]],
  "customer_notifications.waitlist_approved_chain": [["email"]],
  "customer_notifications.fallback_delay_ms": 1e3,
  "customer_notifications.max_chain_length": 5,
  // Premium Features
  "premium_features.appointment_history_limit_days": 3,
  "premium_features.customer_query_limit_enabled": true,
  "premium_features.reports_access_limit_enabled": true,
  "premium_features.ai_chat_enabled": false,
  "premium_features.ai_monthly_credit_default": 0,
  // UI Feature Flags
  "ui.report.enabled": true
};

// src/configuration/configuration-sections.ts
var CONFIG_SECTIONS = [
  {
    id: "salon-config",
    order: 1,
    configKeys: [
      "salon.appointment_worker_assignment_enabled",
      "salon.outcome_tracking_enabled",
      "salon.outcome_tracking_mode"
    ]
  },
  {
    id: "online-booking",
    order: 2,
    configKeys: [
      "online_booking.enabled",
      "online_booking.slots_per_hour",
      "online_booking.lead_minutes_from_now",
      "online_booking.lead_minutes_from_opening",
      "online_booking.trail_minutes_before_closing",
      "online_booking.advance_booking_days",
      "online_booking.worker_selection_enabled",
      "online_booking.service_selection_mode",
      "online_booking.show_service_duration",
      "online_booking.bank_transfer_enabled"
    ]
  },
  {
    id: "booking-behavior",
    order: 3,
    configKeys: [
      "booking_behavior.mode",
      "booking_behavior.manual.count_pending_toward_capacity",
      "booking_behavior.auto.arrange_workers",
      "booking_behavior.auto.confirm_immediately",
      "booking_behavior.auto.batch_confirm_interval"
    ]
  },
  {
    id: "appointment-limits",
    order: 4,
    configKeys: [
      "appointment_limits.capacity_mode",
      "appointment_limits.concurrent_max",
      "appointment_limits.daily_max",
      "appointment_limits.concurrent_infinity",
      "appointment_limits.daily_infinity",
      "appointment_limits.duration_aware"
    ]
  },
  {
    id: "dashboard-features",
    order: 5,
    configKeys: ["ui.report.enabled"]
  }
];
function getConfigurationSection(id) {
  const section = CONFIG_SECTIONS.find((s) => s.id === id);
  if (!section) {
    throw new Error(`Unknown configuration section: ${id}`);
  }
  return section;
}

// src/configuration/configuration-types.ts
var ConfigurationType = /* @__PURE__ */ ((ConfigurationType2) => {
  ConfigurationType2["REGULAR"] = "regular";
  ConfigurationType2["PREMIUM"] = "premium";
  return ConfigurationType2;
})(ConfigurationType || {});
var PREMIUM_CONFIGURATION_KEYS = [
  "notification_limits.email_monthly_limit",
  "notification_limits.sms_monthly_limit",
  "notification_limits.whatsapp_monthly_limit",
  "premium_features.appointment_history_limit_days",
  "premium_features.customer_query_limit_enabled",
  "premium_features.reports_access_limit_enabled",
  "premium_features.ai_chat_enabled"
];
function getConfigurationType(key) {
  return PREMIUM_CONFIGURATION_KEYS.includes(key) ? "premium" /* PREMIUM */ : "regular" /* REGULAR */;
}
function isPremiumConfiguration(key) {
  return PREMIUM_CONFIGURATION_KEYS.includes(key);
}
function getPremiumConfigurationKeys() {
  return PREMIUM_CONFIGURATION_KEYS;
}

// src/configuration/configuration-utils.ts
var ENUM_VALUES = {
  "booking_behavior.mode": Object.values(BookingBehaviorMode),
  "online_booking.mode": Object.values(OnlineBookingMode),
  "salon.outcome_tracking_mode": Object.values(OutcomeTrackingMode),
  "online_booking.service_selection_mode": Object.values(ServiceSelectionMode),
  "online_booking.worker_selection_mode": Object.values(WorkerSelectionMode),
  "appointment_limits.capacity_mode": ["simple", "advanced"]
};
function parseConfigurationValue(key, stringValue) {
  const defaultValue = CONFIG_DEFAULTS[key];
  if (typeof defaultValue === "number") {
    const parsed = parseInt(stringValue, 10);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  } else if (typeof defaultValue === "boolean") {
    return stringValue.toLowerCase() === "true";
  } else if (Array.isArray(defaultValue)) {
    return JSON.parse(stringValue);
  } else {
    const allowedValues = ENUM_VALUES[key];
    if (allowedValues) {
      const normalized = stringValue.toLowerCase();
      if (allowedValues.includes(normalized)) {
        return normalized;
      }
      return defaultValue;
    }
    return stringValue;
  }
}
function serializeConfigurationValue(_key, value) {
  if (Array.isArray(value)) {
    return value.join(",");
  } else {
    return String(value);
  }
}
function validateConfigurationValue(key, value) {
  const defaultValue = CONFIG_DEFAULTS[key];
  if (typeof defaultValue === "number") {
    return typeof value === "number" && !Number.isNaN(value);
  } else if (typeof defaultValue === "boolean") {
    return typeof value === "boolean";
  } else if (Array.isArray(defaultValue)) {
    return Array.isArray(value) && value.every((item) => typeof item === "string");
  } else {
    if (typeof value !== "string") {
      return false;
    }
    const allowedValues = ENUM_VALUES[key];
    if (allowedValues) {
      return allowedValues.includes(value);
    }
    return true;
  }
}
function getAllowedEnumValues(key) {
  return ENUM_VALUES[key];
}
function getCategoryFromKey(key) {
  const parts = key.split(".");
  return parts[0] || "general";
}
function getDataTypeFromKey(key) {
  const defaultValue = CONFIG_DEFAULTS[key];
  if (typeof defaultValue === "number") {
    return "int";
  } else if (typeof defaultValue === "boolean") {
    return "boolean";
  } else if (Array.isArray(defaultValue)) {
    return "string";
  } else if (key.includes("time") || key.includes("cutoff")) {
    return "time";
  } else {
    return "string";
  }
}

// src/currency.ts
var Currency = /* @__PURE__ */ ((Currency2) => {
  Currency2["USD"] = "USD";
  Currency2["EUR"] = "EUR";
  Currency2["VND"] = "VND";
  Currency2["JPY"] = "JPY";
  Currency2["PLN"] = "PLN";
  return Currency2;
})(Currency || {});
var CURRENCY_INFO = {
  ["USD" /* USD */]: { code: "USD" /* USD */, name: "US Dollar", symbol: "$", locale: "en-US" },
  ["EUR" /* EUR */]: { code: "EUR" /* EUR */, name: "Euro", symbol: "\u20AC", locale: "de-DE" },
  ["VND" /* VND */]: { code: "VND" /* VND */, name: "Vietnamese Dong", symbol: "\u20AB", locale: "vi-VN" },
  ["JPY" /* JPY */]: { code: "JPY" /* JPY */, name: "Japanese Yen", symbol: "\xA5", locale: "ja-JP" },
  ["PLN" /* PLN */]: { code: "PLN" /* PLN */, name: "Polish Zloty", symbol: "z\u0142", locale: "pl-PL" }
};
function getSupportedCurrencies() {
  return Object.values(Currency);
}
function isValidCurrency(code) {
  return Object.values(Currency).includes(code);
}
function getCurrencyInfo(code) {
  return CURRENCY_INFO[code];
}
var ZERO_DECIMAL_CURRENCIES = /* @__PURE__ */ new Set([
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "ISK",
  "JPY",
  "KMF",
  "KRW",
  "PYG",
  "RWF",
  "UGX",
  "UYI",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF"
]);
function currencyDivisor(currencyCode) {
  return ZERO_DECIMAL_CURRENCIES.has(currencyCode.toUpperCase()) ? 1 : 100;
}
function toPriceMicros(smallestUnitAmount, currencyCode) {
  return smallestUnitAmount * (1e6 / currencyDivisor(currencyCode));
}

// src/language.ts
var Language = /* @__PURE__ */ ((Language2) => {
  Language2["EN"] = "en";
  Language2["DE"] = "de";
  Language2["VI"] = "vi";
  Language2["PL"] = "pl";
  Language2["CS"] = "cs";
  return Language2;
})(Language || {});
var DEFAULT_LANGUAGE = "en" /* EN */;
var LANGUAGE_INFO = {
  ["en" /* EN */]: { code: "en" /* EN */, name: "English", nativeName: "English" },
  ["de" /* DE */]: { code: "de" /* DE */, name: "German", nativeName: "Deutsch" },
  ["vi" /* VI */]: { code: "vi" /* VI */, name: "Vietnamese", nativeName: "Ti\u1EBFng Vi\u1EC7t" },
  ["pl" /* PL */]: { code: "pl" /* PL */, name: "Polish", nativeName: "Polski" },
  ["cs" /* CS */]: { code: "cs" /* CS */, name: "Czech", nativeName: "\u010Ce\u0161tina" }
};
var getLanguageInfo = (code) => {
  return LANGUAGE_INFO[code];
};
var isValidLanguage = (code) => {
  return Object.values(Language).includes(code);
};

// src/i18n-utils.ts
function getLocalizedText(translations, language) {
  if (!translations || typeof translations !== "object") {
    return "";
  }
  const requestedValue = translations[language];
  if (requestedValue !== void 0) {
    return requestedValue;
  }
  if (language !== "en" /* EN */) {
    const englishValue = translations["en" /* EN */];
    if (englishValue !== void 0) {
      return englishValue;
    }
  }
  const keys = Object.keys(translations);
  if (keys.length > 0) {
    const firstKey = keys[0];
    if (firstKey !== void 0) {
      const firstValue = translations[firstKey];
      if (firstValue !== void 0) {
        return firstValue;
      }
    }
  }
  return "";
}

// src/onboarding/onboarding-types.ts
var OnboardingSessionStatus = /* @__PURE__ */ ((OnboardingSessionStatus2) => {
  OnboardingSessionStatus2["CREATED"] = "CREATED";
  OnboardingSessionStatus2["BASICS_COMPLETE"] = "BASICS_COMPLETE";
  OnboardingSessionStatus2["HOURS_COMPLETE"] = "HOURS_COMPLETE";
  OnboardingSessionStatus2["MENU_UPLOADING"] = "MENU_UPLOADING";
  OnboardingSessionStatus2["AI_PROCESSING"] = "AI_PROCESSING";
  OnboardingSessionStatus2["REVIEW"] = "REVIEW";
  OnboardingSessionStatus2["COMPLETED"] = "COMPLETED";
  OnboardingSessionStatus2["PAUSED"] = "PAUSED";
  OnboardingSessionStatus2["FAILED"] = "FAILED";
  return OnboardingSessionStatus2;
})(OnboardingSessionStatus || {});
var OnboardingMenuSource = /* @__PURE__ */ ((OnboardingMenuSource2) => {
  OnboardingMenuSource2["PHOTO"] = "PHOTO";
  OnboardingMenuSource2["PDF"] = "PDF";
  OnboardingMenuSource2["URL"] = "URL";
  OnboardingMenuSource2["TEXT"] = "TEXT";
  OnboardingMenuSource2["NONE"] = "NONE";
  return OnboardingMenuSource2;
})(OnboardingMenuSource || {});

// src/service-pricing.ts
function hasSelectableOptions(groups) {
  return groups.some((group) => group.options.length > 0);
}
function minRequiredAddition(groups, pick) {
  let total = 0;
  for (const group of groups) {
    if (group.minSelections <= 0 || group.options.length === 0) {
      continue;
    }
    const sorted = group.options.map(pick).sort((a, b) => a - b);
    for (let i = 0; i < Math.min(group.minSelections, sorted.length); i++) {
      total += sorted[i];
    }
  }
  return total;
}
function minRequiredPriceAddition(groups) {
  return minRequiredAddition(groups, (option) => option.priceModifier);
}
function minRequiredDurationAddition(groups) {
  return minRequiredAddition(groups, (option) => option.durationModifier);
}
function calcStartingPrice(basePrice, groups) {
  return basePrice + minRequiredPriceAddition(groups);
}
function calcStartingDuration(durationMinutes, groups) {
  return durationMinutes + minRequiredDurationAddition(groups);
}

// src/social-platform.ts
var SOCIAL_PLATFORMS = ["WEBSITE", "FACEBOOK", "INSTAGRAM", "TIKTOK", "YOUTUBE"];
var SOCIAL_PLATFORM_LABELS = {
  WEBSITE: "Website",
  FACEBOOK: "Facebook",
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  YOUTUBE: "YouTube"
};
var SOCIAL_PLATFORM_ICON_PATHS = {
  WEBSITE: [
    "M2 12a10 10 0 1 0 20 0a10 10 0 1 0-20 0",
    "M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20",
    "M2 12h20"
  ],
  FACEBOOK: ["M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"],
  INSTAGRAM: [
    "M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z",
    "M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z",
    "M17.5 6.5h.01"
  ],
  TIKTOK: ["M9 12a4 4 0 1 0 4 4V2a5 5 0 0 0 5 5"],
  YOUTUBE: [
    "M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17",
    "m10 15 5-3-5-3z"
  ]
};
export {
  ALL_CLIENT_TOOLS,
  ALL_CONFIG_KEYS,
  AUTHORIZATION_ACTIONS,
  AUTHORIZATION_SUBJECTS,
  AgentActionInputSchema,
  AgentCardSchema,
  AgentMessageSchema,
  AgentThreadSchema,
  AgentUserContextSchema,
  AppStateSchema,
  AppointmentAuditMetadataSchema,
  BaseMessageSchema,
  BookingBehaviorMode,
  CLIENT_TOOLS_REGISTRY,
  COMPLETED_APPOINTMENT_FILTER,
  CONFIG_CATEGORIES,
  CONFIG_DEFAULTS,
  CONFIG_KEYS,
  CONFIG_SECTIONS,
  CONFIG_STRING_DEFAULTS,
  CURRENCY_INFO,
  ConfigCardPayloadSchema,
  ConfigurationDraftSchema,
  ConfigurationType,
  CreateThreadInputSchema,
  Currency,
  DEFAULT_LANGUAGE,
  HighlightElementSchema,
  LANGUAGE_INFO,
  Language,
  MessageRoleSchema,
  NavigateToPageSchema,
  NodeResultSchema,
  NotificationChannel,
  OnboardingMenuSource,
  OnboardingSessionStatus,
  OnlineBookingMode,
  OpenSettingsPanelSchema,
  OutcomeTrackingMode,
  PREMIUM_CONFIGURATION_KEYS,
  ROLE_ABILITIES,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORM_ICON_PATHS,
  SOCIAL_PLATFORM_LABELS,
  SQL_COMPLETED_APPOINTMENT_CONDITION,
  SendMessageInputSchema,
  ServiceSelectionMode,
  ShowAppointmentDetailSchema,
  ShowNotificationSchema,
  ThreadStatusSchema,
  TypedAgentCardSchema,
  UIStateSchema,
  UNLIMITED_CAPACITY,
  UpdateCalendarViewSchema,
  WorkerSelectionMode,
  ZERO_DECIMAL_CURRENCIES,
  calcStartingDuration,
  calcStartingPrice,
  createAbilityForUser,
  createAppointmentAuditMetadata,
  createPartnerGroupAbilityForUser,
  createSalonAbilityForUser,
  createSystemAbility,
  currencyDivisor,
  defineFranchiseAdminAbilities,
  defineManagerAbilities,
  defineOwnerAbilities,
  definePartnerGroupAdminAbilities,
  definePartnerGroupMemberAbilities,
  defineSuperAdminAbilities,
  defineWorkerAbilities,
  detectSubjectType,
  getAllowedEnumValues,
  getCategoryFromKey,
  getConfigurationSection,
  getConfigurationType,
  getCurrencyInfo,
  getDataTypeFromKey,
  getLanguageInfo,
  getLocalizedText,
  getPremiumConfigurationKeys,
  getSupportedCurrencies,
  hasSelectableOptions,
  highlightElementTool,
  isAppointmentCancelled,
  isAppointmentCompleted,
  isAuthorizationAction,
  isAuthorizationSubject,
  isPremiumConfiguration,
  isValidConfigurationKey,
  isValidCurrency,
  isValidLanguage,
  minRequiredDurationAddition,
  minRequiredPriceAddition,
  navigateToPageTool,
  openSettingsPanelTool,
  parseConfigurationValue,
  serializeConfigurationValue,
  showAppointmentDetailTool,
  showNotificationTool,
  sqlCompletedCondition,
  toPriceMicros,
  updateCalendarViewTool,
  validateAppointmentAuditMetadata,
  validateConfigurationValue
};
//# sourceMappingURL=index.mjs.map